import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api, message, type Client, type Meeting } from "../api";
import { relayAudio, type RelayProgress } from "../relay";
import { ProcessorStatus } from "../ProcessorStatus";
import { SourceLink } from "../SourceLink";

const STATUS_LABEL: Record<string, string> = {
  queued: "Queued", resolving: "Resolving source",
  needs_relay: "Needs browser fetch", relaying: "Fetching via browser",
  ingesting: "Downloading", demuxing: "Extracting audio",
  transcribing: "Transcribing", identifying: "Identifying speakers",
  fusing: "Attributing speakers",
  segmenting: "Finding meetings", drafting: "Drafting minutes",
  enrolling: "Remembering voices",
  review: "Ready for review", published: "Published", failed: "Failed",
};

function mb(n: number) { return (n / 1048576).toFixed(1); }

/** The meeting's own date, in the city's time zone: "Oct 26, 2026". */
function meetingDay(m: Meeting) {
  if (!m.meetingDate) return null;
  try {
    return new Date(m.meetingDate).toLocaleDateString(undefined, { timeZone: m.clientTimezone, month: "short", day: "numeric", year: "numeric" });
  } catch {
    return new Date(m.meetingDate).toLocaleDateString();
  }
}

const SHOWING = "meetings.client";
const remembered = () => { try { return localStorage.getItem(SHOWING) ?? ""; } catch { return ""; } };

export function Meetings() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Meeting[] | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relaying, setRelaying] = useState<Record<string, RelayProgress>>({});
  const [confirming, setConfirming] = useState<string | null>(null);
  // Which client's meetings the list shows: "" for all of them.
  const [showing, setShowing] = useState(remembered);
  // The polling below reads this, not a stale copy of the state.
  const showingRef = useRef(showing);

  async function load(only = showingRef.current) {
    try {
      const [m, c] = await Promise.all([
        api.get<Meeting[]>(only ? `/api/meetings?clientId=${encodeURIComponent(only)}` : "/api/meetings"),
        api.get<Client[]>("/api/clients"),
      ]);
      // A client no longer visible to this user: show everything instead.
      if (only && !c.some((x) => x.id === only)) { show(""); return; }
      setRows(m);
      setClients(c);
      if (c[0] && !clientId) setClientId(only || c[0].id);
    } catch (err) {
      setError(message(err));
      setRows([]);
    }
  }

  function show(id: string) {
    setShowing(id);
    showingRef.current = id;
    try { localStorage.setItem(SHOWING, id); } catch { /* private window: not remembered */ }
    setRows(null);
    void load(id);
  }

  useEffect(() => { void load(); }, []);

  // Follow the worker while anything is moving; stop when all is settled.
  const moving = (rows ?? []).some((m) =>
    ["ingesting", "demuxing", "transcribing", "fusing", "identifying", "segmenting", "drafting", "enrolling", "resolving"].includes(m.status) ||
    (m.status === "queued" && m.hasAudio));
  useEffect(() => {
    if (!moving) return;
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [moving]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let created: { id: string };
      try {
        created = await api.post<{ id: string }>("/api/meetings", { clientId, sourceUrl });
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== "duplicate_recording") throw err;
        const prior = err.body.existing as { id: string; status: string; createdAt: string };
        const when = new Date(prior.createdAt).toLocaleString();
        if (window.confirm(
          `This recording was already submitted for this client on ${when} ` +
          `(${STATUS_LABEL[prior.status] ?? prior.status}).\n\nOpen that one instead?`)) {
          navigate(`/meetings/${prior.id}`);
          return;
        }
        if (!window.confirm("Submit it again anyway? It will be transcribed — and billed — a second time.")) return;
        created = await api.post<{ id: string }>("/api/meetings", { clientId, sourceUrl, allowDuplicate: true });
      }
      setSourceUrl("");
      setOpen(false);
      // Resolve immediately so the operator learns straight away whether the
      // source needs a browser fetch, rather than discovering it later.
      await api.post(`/api/meetings/${created.id}/resolve`).catch(() => {});
      await load();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  async function resolve(id: string) {
    setError(null);
    try { await api.post(`/api/meetings/${id}/resolve`); await load(); }
    catch (err) { setError(message(err)); await load(); }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await api.del(`/api/meetings/${id}`);
      setConfirming(null);
      await load();
    } catch (err) {
      setError(message(err));
    }
  }

  async function fetchViaBrowser(m: Meeting) {
    const id = m.id;
    setError(null);
    try {
      await relayAudio(m, (p) => setRelaying((r) => ({ ...r, [id]: p })));
      await load();
    } catch (err) {
      setError(message(err));
      await load();
    } finally {
      setRelaying((r) => { const n = { ...r }; delete n[id]; return n; });
    }
  }

  return (
    <>
      <div className="pagehead">
        <h1>Meetings</h1>
        <button className="btn btn--inline" onClick={() => setOpen((v) => !v)}>
          {open ? "Cancel" : "New meeting"}
        </button>
      </div>

      <ProcessorStatus />
      {error && <div className="alert" role="alert">{error}</div>}

      {clients.length > 1 && (
        <div className="filterbar">
          <label htmlFor="showing">Show</label>
          <select id="showing" value={showing} onChange={(e) => show(e.target.value)}>
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {open && (
        <form className="card card--form" onSubmit={submit}>
          <div className="field">
            <label htmlFor="client">Client</label>
            <select id="client" value={clientId} required
                    onChange={(e) => setClientId(e.target.value)}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label htmlFor="url">Recording URL</label>
            <input id="url" type="url" required placeholder="https://…"
                   value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} />
            <p className="hint">
              YouTube, Zoom, Webex, Cablecast, Dropbox, OneDrive or a direct
              file. One recording may contain several meetings — they're split
              into separate minutes after transcription.
            </p>
          </div>

          <button className="btn" type="submit" disabled={busy || !clientId}>
            {busy ? "Submitting…" : "Submit for processing"}
          </button>
        </form>
      )}

      {rows === null ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 && showing ? (
        <div className="card empty">
          <p><strong>No meetings for {clients.find((c) => c.id === showing)?.name ?? "this client"} yet.</strong></p>
          <p className="muted"><button className="linkbtn" onClick={() => show("")}>Show all clients</button></p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card empty">
          <p><strong>No meetings yet.</strong></p>
          <p className="muted">
            Submit a recording URL and it will be transcribed, attributed and
            drafted into the client's minutes template.
          </p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Client</th><th>Meeting date</th><th>Source</th><th>Status</th><th>Meetings</th><th>Added</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const p = relaying[m.id];
              return (
                <tr key={m.id}>
                  <td>{m.clientName}</td>
                  <td className="nowrap">{meetingDay(m) ?? <span className="muted">Not known</span>}</td>
                  <td>
                    <Link to={`/meetings/${m.id}`}>
                      {m.title ?? <span className="muted">Untitled</span>}
                    </Link>
                    <div className="small muted">
                      <SourceLink url={m.sourceUrl} label={m.platform ? `${m.platform} source` : "Source"} />
                    </div>
                  </td>
                  <td>
                    <span className={`status status--${m.status}`}>
                      {STATUS_LABEL[m.status] ?? m.status}
                    </span>
                    {m.statusDetail && <div className="muted small">{m.statusDetail}</div>}
                    {m.hasAudio && <div className="ok small">✓ Audio stored</div>}

                    {p && (
                      <div className="relay">
                        <div className="relay__bar">
                          <div
                            className="relay__fill"
                            style={{
                              width: p.totalBytes
                                ? `${Math.round((p.bytesSent / p.totalBytes) * 100)}%`
                                : "10%",
                            }}
                          />
                        </div>
                        <span className="muted small">
                          {p.phase === "discovering" ? "Finding audio track…"
                            : p.phase === "finishing" ? "Finishing…"
                            : `${mb(p.bytesSent)} / ${p.totalBytes ? mb(p.totalBytes) : "?"} MB`}
                          {p.fromVideo && (
                            <span className="relay-note">
                              {" "}The station's audio track stops at {Math.round(p.fromVideo.audioMinutes)} of{" "}
                              {Math.round(p.fromVideo.videoMinutes)} minutes, so the sound is being read out of
                              the full video instead. This takes 20–30 minutes — keep this tab open.
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    {!p && (
                      <div className="actions">
                        {m.status === "needs_relay" && (
                          <button className="linkbtn" onClick={() => void fetchViaBrowser(m)}>
                            Fetch via my browser
                          </button>
                        )}
                        {/* "relaying" with no transfer running in this tab means
                            it was interrupted -- tab closed, network dropped. The
                            server aborts the stale upload when this restarts it. */}
                        {m.status === "relaying" && (
                          <button className="linkbtn" onClick={() => void fetchViaBrowser(m)}>
                            Interrupted? Restart fetch
                          </button>
                        )}
                        {/* Not once audio is stored: re-checking a WAF-blocked
                            source would knock the row back to needs-fetch. */}
                        {!m.hasAudio && (m.status === "failed" || m.status === "queued") && (
                          <button className="linkbtn" onClick={() => void resolve(m.id)}>
                            Re-resolve
                          </button>
                        )}
                        {m.deletable && confirming !== m.id && (
                          <button className="linkbtn" onClick={() => setConfirming(m.id)}>
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                    {!p && m.deletable && confirming === m.id && (
                      <div className="confirm" role="alertdialog" aria-label="Confirm delete">
                        <span className="small">
                          Delete this submission{m.hasAudio ? " and its stored audio" : ""}?
                          This can't be undone.
                        </span>
                        <div className="actions">
                          <button className="linkbtn linkbtn--danger" onClick={() => void remove(m.id)}>
                            Yes, delete
                          </button>
                          <button className="linkbtn" onClick={() => setConfirming(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {m.segments.length === 0
                      ? <span className="muted">—</span>
                      : m.segments.map((s) => (
                          <span key={s.code} className="pill pill--quiet">{s.code}</span>
                        ))}
                  </td>
                  <td className="muted small">
                    {new Date(m.createdAt).toLocaleDateString()}
                    <div>{new Date(m.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
